import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import type { lossAversionModalQuery } from '@atlassian/jira-relay/src/__generated__/lossAversionModalQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { SplitPanelModal } from './split-panel';
import { StorageModal } from './storage';
import { UserLimitModal } from './user-limit';

export const JiraLossAversionModal = () => (
	<JSErrorBoundary
		id="jiraLossAversionModal"
		packageName="jiraLossAversion"
		teamName="luna-growth"
		fallback="unmount"
	>
		<div data-testid="loss-aversion.ui.loss-aversion-modal.jira-loss-aversion-modal-content">
			<JiraLossAversionModalContent />
		</div>
	</JSErrorBoundary>
);
const GB_IN_BYTES = 1_000_000_000;

const bytesToGB = (bytes: number) => bytes / GB_IN_BYTES;

export const JiraLossAversionModalContent = () => {
	const cloudId = useCloudId();

	const data = useLazyLoadQuery<lossAversionModalQuery>(
		graphql`
			query lossAversionModalQuery($cloudId: ID!) {
				jira {
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: JIRA_SOFTWARE)
				}
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "jira-software.ondemand") {
						entitlement {
							subscription {
								accountDetails {
									invoiceGroup {
										experienceCapabilities {
											configurePayment {
												experienceUrl
												isAvailableToUser
											}
										}
									}
								}
							}
						}
						... on CommerceEntitlementInfoCcp {
							__typename
							entitlement {
								subscription {
									chargeDetails {
										chargeQuantities {
											quantity
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId },
	);

	const entitlementInfo = data?.tenantContexts?.[0]?.entitlementInfo;

	const entitlement = entitlementInfo?.entitlement;
	const configurePayment =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePayment;

	// This might come back as null from graphql but we want to default to undefined to make the types easier to work with
	const upgradeUrl = configurePayment?.experienceUrl || undefined;

	const userCanPay = !!configurePayment?.isAvailableToUser;

	const userCount =
		entitlementInfo?.__typename === 'CommerceEntitlementInfoCcp'
			? entitlementInfo.entitlement?.subscription?.chargeDetails?.chargeQuantities?.[0]?.quantity ||
				1
			: 1;

	if (userCount > 10) {
		return <UserLimitModal userCanPay={userCanPay} userCount={userCount} />;
	}

	const attachmentStorageUsed = data?.jira?.attachmentStorageUsed || 0;
	const storageUsedInGB = bytesToGB(attachmentStorageUsed);

	if (attachmentStorageUsed > 2 * GB_IN_BYTES) {
		return (
			<StorageModal
				userCanPay={userCanPay}
				upgradeUrl={upgradeUrl}
				storageUsedInGB={storageUsedInGB}
			/>
		);
	}

	return <SplitPanelModal userCanPay={userCanPay} upgradeUrl={upgradeUrl} />;
};
